import * as Yup from 'yup';
import { Formik } from 'formik';
import React, { Fragment, createRef } from 'react';
import { ModalComponent } from 'components/layout/modals/ModalComponent';
import { applicantStore } from '../ApplicantStore';
import './ApplicantResumeUpload.sass';

const ACCEPTED_FORMATS = ['application/pdf'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const ValidationSchema = Yup.object().shape({
	resume: Yup.mixed()
		.required()
		.test('fileFormat', 'Only PDF files are allowed', value => {
			if (!value) return false;
			return ACCEPTED_FORMATS.includes(value.type);
		})
		.test('fileSize', 'File size must be less than 5MB', value => {
			if (!value) return false;
			return value.size <= MAX_FILE_SIZE;
		})
		.label('Resume')
});

export default class ApplicantResumeUpload extends ModalComponent {
	constructor(props) {
		super(props);
		this.state = {
			...this.state,
			isDragging: false
		};
		this.fileInputRef = createRef();
	}

	getInitialValues() {
		return {
			resume: null
		};
	}

	handleDragEnter = e => {
		e.preventDefault();
		e.stopPropagation();
		this.setState({ isDragging: true });
	};

	handleDragLeave = e => {
		e.preventDefault();
		e.stopPropagation();
		this.setState({ isDragging: false });
	};

	handleDragOver = e => {
		e.preventDefault();
		e.stopPropagation();
	};

	handleDrop = (e, formikBag) => {
		e.preventDefault();
		e.stopPropagation();
		this.setState({ isDragging: false });

		const droppedFile = e.dataTransfer.files[0];
		if (droppedFile) {
			formikBag.setFieldTouched('resume', true);
			formikBag.setFieldValue('resume', droppedFile);
		}
	};

	handleFileInputChange = (e, formikBag) => {
		const selectedFile = e.target.files[0];
		if (selectedFile) {
			formikBag.setFieldTouched('resume', true);
			formikBag.setFieldValue('resume', selectedFile);
		}
	};

	handleSelectFilesClick = () => {
		this.fileInputRef.current?.click();
	};

	handleRemoveFile = formikBag => {
		formikBag.setFieldTouched('resume', true);
		formikBag.setFieldValue('resume', null);
		if (this.fileInputRef.current) {
			this.fileInputRef.current.value = '';
		}
	};

	render() {
		return (
			<Formik
				initialValues={this.getInitialValues()}
				validationSchema={ValidationSchema}
				onSubmit={values => {
					this.saving();
					this.uploadResume(values);
				}}>
				{formikBag =>
					this.renderModalContainer(formikBag, 'ApplicantResumeUpload')
				}
			</Formik>
		);
	}

	renderForm(formikBag) {
		const { isDragging } = this.state;
		const file = formikBag.values.resume;

		return (
			<Fragment>
				<div
					className={`dropzone ${isDragging ? 'is-dragging' : ''} ${file ? 'has-file' : ''}`}
					onDragEnter={this.handleDragEnter}
					onDragLeave={this.handleDragLeave}
					onDragOver={this.handleDragOver}
					onDrop={e => this.handleDrop(e, formikBag)}>
					<input
						ref={this.fileInputRef}
						type="file"
						accept=".pdf,application/pdf"
						onChange={e => this.handleFileInputChange(e, formikBag)}
						className="file-input-hidden"
					/>

					<div className="dropzone-content">
						<div className="dropzone-icon">
							<img src="/ats/icons/documents-icon.svg" alt="Document" />
						</div>

						{file ? (
							<div className="file-info">
								<span className="file-name">{file.name}</span>
								<button
									type="button"
									className="remove-file-btn"
									onClick={() => this.handleRemoveFile(formikBag)}>
									<img src="/ats/icons/close-blue-icon.svg" alt="Remove" />
								</button>
							</div>
						) : (
							<>
								<p className="dropzone-text">
									Drag and drop a file here to upload
								</p>
								<button
									type="button"
									className="select-files-btn"
									onClick={this.handleSelectFilesClick}>
									Select File
								</button>
							</>
						)}
					</div>
				</div>

				{formikBag.touched.resume && formikBag.errors.resume && (
					<p className="error-message">{formikBag.errors.resume}</p>
				)}

				<p className="upload-disclaimer">
					By uploading a resume, you confirm you have permission to share this
					information.
				</p>
			</Fragment>
		);
	}

	async uploadResume(values) {
		const result = await applicantStore.uploadResume(
			this.props.applicantId,
			values.resume
		);

		this.resultNotification(
			result,
			'Resume',
			'Upload',
			'Upload Resume',
			'The resume has been successfully uploaded.'
		);
	}
}
